import { useMemo, useState } from 'react';

import { useSubscribe } from '@atlassian/graphql-subscription';

import type {
	AdapterSubscriptionConfig,
	SerializableRecord,
	SubscriptionAdapter,
	SubscriptionAdapterResult,
} from '../types';

const NOOP_PAYLOAD: {
	query: string;
	operationName: string;
	variables: Record<string, unknown>;
} = {
	query: 'subscription NoOp { __typename }',
	operationName: 'NoOp',
	variables: {},
};

function useRelaySubscription(
	config: AdapterSubscriptionConfig,
): SubscriptionAdapterResult<SerializableRecord> {
	const query =
		config.subscription !== undefined && typeof config.subscription === 'string'
			? config.subscription
			: null;
	const [state, setState] = useState<{
		data: SerializableRecord | null;
		error: Error | null;
		isLoading: boolean;
	}>({ data: null, error: null, isLoading: true });

	const payload = useMemo(() => {
		if (query) {
			const operationName = query.match(/subscription\s+(\w+)/)?.[1] ?? '';
			return {
				query,
				operationName,
				variables: config.variables,
			};
		}
		return { ...NOOP_PAYLOAD, variables: config.variables };
	}, [query, config.variables]);

	const sink = useMemo(
		() => ({
			next: (value: { data?: SerializableRecord }) => {
				const data = value?.data;
				if (data != null) {
					setState({ data, error: null, isLoading: false });
				}
			},
			error: (err: Error) => {
				setState({ data: null, error: err, isLoading: false });
			},
		}),
		[],
	);

	useSubscribe<{ data?: SerializableRecord }>(payload, sink);

	if (!query) {
		return {
			status: 'loading',
			data: null,
			error: null,
			isLoading: true,
		};
	}
	if (state.error) {
		return {
			status: 'error',
			data: null,
			error: state.error,
			isLoading: false,
		};
	}
	if (state.data) {
		return {
			status: 'success',
			data: state.data,
			error: null,
			isLoading: false,
		};
	}
	return {
		status: 'loading',
		data: null,
		error: null,
		isLoading: true as const,
	};
}

/**
 * Creates a subscription adapter using useSubscribe from @atlassian/graphql-subscription.
 * Expects config.subscription to be the query string (e.g. from getPlacementSubscriptionDocument).
 *
 * @returns SubscriptionAdapter for use with StableSubscriptionAdapterProvider
 */
export const createUseSubscribeAdapter = (): SubscriptionAdapter<SerializableRecord> => ({
	useSubscription: useRelaySubscription,
});
